import axios from 'axios'
import {FC, FormEvent, ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {Link, Navigate, useNavigate} from 'react-router-dom'
import {API_URL} from '../../../../../_metronic/helpers'
import {SUPPORT_PHONE, SUPPORT_PHONE_LABEL} from '../../../../../_metronic/helpers/supportContact'
import {PERMISSION_LABELS} from '../../../apps/tax-workspace/core/enums'
import {TermsViewerButton} from '../../../terms/components/TermsViewerButton'
import {TermsDocument} from '../../../terms/core/_models'
import {acceptTerms, getCurrentTerms} from '../../../terms/core/_requests'
import {formatTermsVersion} from '../../../terms/core/termsFormat'
import {useAuth} from '../../core/Auth'
import {AuthModel} from '../../core/_models'
import {getUserByToken} from '../../core/_requests'
import {useRegistrationView} from '../../core/RegisterationProvider'
import {RegistrationSignOut} from './RegistrationSignOut'
import {SSO_BUSINESS_REGISTRATION_PATH, isSsoSession} from '../../core/ssoBusinessRegistration'
import {
  CONSULTATION_PATH,
  ONLINE_STEPS,
  OnlineAvailability,
  OnlineDelegation,
  OnlineFailure,
  OnlineIdentifyResult,
  OnlineMemoryResult,
  OnlineSession,
  OnlineStep,
  OnlineTaxFile,
  describeIdentifier,
  formatJalaliDate,
  formatRemaining,
  hasNoBusiness,
  isCompleteFiscalIdValue,
  isCompleteOtp,
  isSearchableIdentifier,
  isSupportSession,
  normalizeFiscalId,
  normalizeIdentifierInput,
  normalizeOtpInput,
  readOnlineFailure,
  stepAfterSession,
  stepAfterVerify,
  stepIndex,
  toPersianNumber,
} from '../../core/onlineRegistration'
import {
  addExistingFiscalMemory,
  getOnlineAvailability,
  identifyTaxFiles,
  issueMemoryUniqueId,
  requestOwnerCode,
  startOnlineRegistration,
  verifyOwnerCode,
} from '../../core/onlineRegistrationRequests'
import {createIdempotencyKey} from './businessRegistration'
import './BusinessRegistration.scss'
import './OnlineRegistration.scss'

type Busy = 'identify' | 'start' | 'otp' | 'verify' | 'memory' | 'open' | null

interface RegisteredBusiness {
  companyId: number
  companyName?: string
  created: boolean
  delegation?: OnlineDelegation
  memoryUniqueIdAvailable: boolean
}

/**
 * The sign-up opened from a partner's link (/join/{token}): every step names the link, a fixed
 * economic code starts at once, and the end goes back to the partner instead of the dashboard.
 */
export interface PartnerRegistration {
  sessionId: string
  partnerName: string
  /** Only this business can be registered through the link. */
  economicCode?: string | null
  /** The identifier the partner gave to list the taxpayer's files by. */
  nationalId?: string | null
  hasReturnUrl: boolean
  /** Completes the link; resolves with the partner's signed return URL (null without one). */
  finish: (companyId: number) => Promise<string | null>
  /** Cancels the link before the panel is made; resolves with the partner's return URL. */
  cancel: () => Promise<string | null>
}

const Spinner: FC<{label: string}> = ({label}) => (
  <>
    <span className='spinner-border spinner-border-sm' aria-hidden='true' /> {label}
  </>
)

/**
 * «ثبت‌نام و ساخت پنل»: identify the business by its tax file, prove the ownership with the tax
 * organisation's code, get the fiscal memory id — the panel is ready. See core/onlineRegistration.
 */
const OnlineBusinessRegistration: FC<{partner?: PartnerRegistration}> = ({partner}) => {
  const {currentUser, saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const {setHelpPlace} = useRegistrationView()
  const noBusiness = hasNoBusiness(currentUser)
  const ssoUser = isSsoSession(currentUser)
  const partnerSessionId = partner?.sessionId
  const fixedEconomicCode = partner?.economicCode || undefined

  const [availability, setAvailability] = useState<OnlineAvailability>()
  const [availabilityState, setAvailabilityState] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading'
  )
  const [unavailableMessage, setUnavailableMessage] = useState<string>()
  const [step, setStep] = useState<OnlineStep>('identify')
  const [identifier, setIdentifier] = useState('')
  const [files, setFiles] = useState<OnlineIdentifyResult>()
  const [session, setSession] = useState<OnlineSession>()
  const [delegation, setDelegation] = useState<OnlineDelegation>()
  const [otp, setOtp] = useState('')
  const [now, setNow] = useState(Date.now)
  const [business, setBusiness] = useState<RegisteredBusiness>()
  const [memory, setMemory] = useState<OnlineMemoryResult>()
  const [memoryMode, setMemoryMode] = useState<'issue' | 'existing'>('issue')
  const [fiscalId, setFiscalId] = useState('')
  const [busy, setBusy] = useState<Busy>(null)
  const [choosing, setChoosing] = useState<string>()
  const [failure, setFailure] = useState<OnlineFailure>()
  const [terms, setTerms] = useState<TermsDocument>()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey)
  // The partner's return: the URL once the link is completed, and the taxpayer's «انصراف» confirmation.
  const [partnerReturn, setPartnerReturn] = useState<{state: 'idle' | 'finishing' | 'ready' | 'failed'; url?: string | null}>({
    state: 'idle',
  })
  const [confirmCancel, setConfirmCancel] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const ssoListed = useRef(false)
  const partnerStarted = useRef(false)

  useEffect(() => {
    setHelpPlace(
      <div className='business-registration-help'>
        <i className='las la-user-check' aria-hidden='true' />
        <h2>ثبت‌نام آنلاین</h2>
        <p>
          پرونده مالیاتی کسب‌وکار از سامانه مؤدیان خوانده می‌شود. سامانه مؤدیان یک رمز یک‌بارمصرف به
          موبایلی که در کارپوشه مالیاتی ثبت شده می‌فرستد؛ با وارد کردن آن، مالکیت شما تأیید و پنل
          ساخته می‌شود و شناسه یکتای حافظه مالیاتی هم از همان‌جا گرفته می‌شود.
        </p>
      </div>
    )
  }, [setHelpPlace])

  // The version and text the terms box accepts; without it the acceptance window asks later.
  useEffect(() => {
    let active = true
    getCurrentTerms()
      .then((document) => {
        if (active) setTerms(document)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true
    getOnlineAvailability()
      .then((result) => {
        if (!active) return
        setAvailability(result)
        setUnavailableMessage(result.message)
        setAvailabilityState(result.available ? 'ready' : 'unavailable')
      })
      .catch((error) => {
        if (!active) return
        const read = readOnlineFailure(error, 'ثبت‌نام آنلاین در حال حاضر در دسترس نیست.')
        // A backend that does not know the online sign-up yet: a person without any business
        // keeps the membership request form they had before (not when a partner sent them).
        if (read.unavailable && noBusiness && !ssoUser && !partnerSessionId) {
          window.location.replace(CONSULTATION_PATH)
          return
        }
        setUnavailableMessage(read.message)
        setAvailabilityState('unavailable')
      })
    return () => {
      active = false
    }
  }, [noBusiness, ssoUser, partnerSessionId])

  useEffect(() => {
    headingRef.current?.focus()
  }, [step])

  // The code's countdown.
  useEffect(() => {
    if (!delegation?.otpPending || !delegation.otpExpiresAt) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [delegation])

  const search = useCallback(
    async (value: string) => {
      setBusy('identify')
      setFailure(undefined)
      try {
        setFiles(await identifyTaxFiles(value, partnerSessionId))
      } catch (error) {
        setFiles(undefined)
        setFailure(readOnlineFailure(error, 'جست‌وجوی پرونده انجام نشد. چند لحظه بعد دوباره تلاش کنید.'))
      } finally {
        setBusy(null)
      }
    },
    [partnerSessionId]
  )

  const choose = useCallback(
    async (file: Pick<OnlineTaxFile, 'economicCode'>) => {
      setBusy('start')
      setChoosing(file.economicCode)
      setFailure(undefined)
      try {
        const started = await startOnlineRegistration(file.economicCode, partnerSessionId)
        setSession(started)
        setDelegation(started.delegation)
        setOtp('')
        setIdempotencyKey(createIdempotencyKey())
        const next = stepAfterSession(started)
        if (next !== 'owner' && started.companyId) {
          setBusiness({
            companyId: started.companyId,
            companyName: started.companyName,
            created: false,
            delegation: started.delegation,
            memoryUniqueIdAvailable: started.memoryUniqueIdAvailable,
          })
        }
        setStep(next)
      } catch (error) {
        setFailure(readOnlineFailure(error, 'انتخاب این پرونده انجام نشد. دوباره تلاش کنید.'))
      } finally {
        setBusy(null)
        setChoosing(undefined)
      }
    },
    [partnerSessionId]
  )

  // A کارپوشه sign-in lists its own files at once (not on a partner's link, which names its own).
  useEffect(() => {
    if (availabilityState === 'ready' && ssoUser && !partnerSessionId && !ssoListed.current) {
      ssoListed.current = true
      search('')
    }
  }, [availabilityState, ssoUser, partnerSessionId, search])

  // A partner's link: a fixed economic code starts at once; a national id lists its files.
  useEffect(() => {
    if (availabilityState !== 'ready' || !partnerSessionId || partnerStarted.current) return
    partnerStarted.current = true
    if (fixedEconomicCode) {
      choose({economicCode: fixedEconomicCode})
    } else if (partner?.nationalId) {
      setIdentifier(partner.nationalId)
      search(partner.nationalId)
    }
  }, [availabilityState, partnerSessionId, fixedEconomicCode, partner?.nationalId, choose, search])

  // The end of a partner's link: complete it once and keep the partner's return URL.
  useEffect(() => {
    if (step !== 'done' || !partner || !business || partnerReturn.state !== 'idle') return
    setPartnerReturn({state: 'finishing'})
    partner
      .finish(business.companyId)
      .then((url) => setPartnerReturn({state: 'ready', url}))
      .catch((error) => {
        setPartnerReturn({state: 'failed'})
        setFailure(readOnlineFailure(error, `بازگشت به ${partner.partnerName} آماده نشد؛ دوباره تلاش کنید.`))
      })
  }, [step, partner, business, partnerReturn.state])

  if (isSupportSession(currentUser)) {
    return <Navigate to='/dashboard' replace />
  }

  const restart = (keepFiles = true) => {
    setStep('identify')
    setSession(undefined)
    setDelegation(undefined)
    setOtp('')
    setIdempotencyKey(createIdempotencyKey())
    if (!keepFiles) setFiles(undefined)
  }

  const cancelPartner = async () => {
    if (!partner) return
    setBusy('open')
    setFailure(undefined)
    try {
      const url = await partner.cancel()
      if (url) {
        window.location.assign(url)
        return
      }
      setConfirmCancel(false)
      setBusy(null)
    } catch (error) {
      setFailure(readOnlineFailure(error, 'لغو ثبت‌نام انجام نشد. دوباره تلاش کنید.'))
      setBusy(null)
    }
  }

  const returnToPartner = () => {
    if (partnerReturn.url) window.location.assign(partnerReturn.url)
  }

  const sendCode = async () => {
    if (!session) return
    if (session.mode === 'create' && !termsAccepted) {
      setFailure({
        message: 'برای ساخت پنل ابتدا قوانین و مقررات را بپذیرید.',
        restart: false,
        newCode: false,
        contactSupport: false,
        unavailable: false,
      })
      return
    }
    setBusy('otp')
    setFailure(undefined)
    try {
      const sent = await requestOwnerCode(session.inquiryId, partnerSessionId)
      setDelegation(sent)
      setOtp('')
      setNow(Date.now())
    } catch (error) {
      const read = readOnlineFailure(error, 'ارسال رمز انجام نشد. چند لحظه بعد دوباره تلاش کنید.')
      setFailure(read)
      if (read.restart) restart()
    } finally {
      setBusy(null)
    }
  }

  const verify = async (event: FormEvent) => {
    event.preventDefault()
    if (!session || !isCompleteOtp(otp, delegation?.otpLength)) return
    setBusy('verify')
    setFailure(undefined)
    try {
      const result = await verifyOwnerCode(session.inquiryId, otp, idempotencyKey, partnerSessionId)
      setBusiness({
        companyId: result.companyId,
        companyName: result.companyName,
        created: result.created,
        delegation: result.delegation,
        memoryUniqueIdAvailable: result.memoryUniqueIdAvailable,
      })
      setDelegation(result.delegation)
      setStep(stepAfterVerify(result))
    } catch (error) {
      const read = readOnlineFailure(error, 'تأیید رمز انجام نشد. دوباره تلاش کنید.')
      setFailure(read)
      setOtp('')
      if (read.newCode && delegation) setDelegation({...delegation, otpPending: false})
      if (read.restart) restart()
    } finally {
      setBusy(null)
    }
  }

  const issueMemory = async () => {
    if (!business) return
    setBusy('memory')
    setFailure(undefined)
    try {
      setMemory(await issueMemoryUniqueId(business.companyId, partnerSessionId))
      setStep('done')
    } catch (error) {
      setFailure(readOnlineFailure(error, 'دریافت شناسه یکتا انجام نشد. دوباره تلاش کنید.'))
    } finally {
      setBusy(null)
    }
  }

  const addMemory = async (event: FormEvent) => {
    event.preventDefault()
    if (!business || !isCompleteFiscalIdValue(fiscalId)) return
    setBusy('memory')
    setFailure(undefined)
    try {
      setMemory(await addExistingFiscalMemory(business.companyId, fiscalId, partnerSessionId))
      setStep('done')
    } catch (error) {
      setFailure(readOnlineFailure(error, 'ثبت شناسه یکتا انجام نشد. دوباره تلاش کنید.'))
    } finally {
      setBusy(null)
    }
  }

  // newlyRegistered: the business was made here with the terms box ticked, so its acceptance is
  // recorded for it once it is the selected business.
  const openBusiness = async (companyId: number, newlyRegistered: boolean) => {
    setBusy('open')
    setFailure(undefined)
    try {
      await axios.post(`${API_URL}/auth/changeCompany`, {compnyId: companyId})
      if (newlyRegistered && termsAccepted && terms) {
        try {
          await acceptTerms({version: terms.version, contentHash: terms.contentHash, channel: 'registration'})
        } catch {
          // Not recorded: the acceptance window asks again on entering the panel.
        }
      }
      const {data: user} = await getUserByToken('')
      saveAuth(user as AuthModel)
      setCurrentUser(user as AuthModel)
      navigate('/dashboard', {replace: true})
    } catch (error) {
      setFailure(
        readOnlineFailure(error, 'کسب‌وکار آماده است، ولی ورود به آن انجام نشد. دوباره روی «ورود به پنل» بزنید.')
      )
      setBusy(null)
    }
  }

  const supportContact = (
    <span>
      پشتیبانی:{' '}
      <a href={`tel:${SUPPORT_PHONE}`} dir='ltr'>
        {SUPPORT_PHONE_LABEL}
      </a>
    </span>
  )

  const failureBox = (extra?: ReactNode) =>
    failure ? (
      <div className='business-alert business-alert--error' role='alert'>
        <i className='las la-exclamation-circle' aria-hidden='true' />
        <div>
          <strong>{failure.message}</strong>
          {failure.contactSupport && <small className='sso-registration__note'>{supportContact}</small>}
          {failure.traceId && (
            <small className='sso-registration__note'>
              شناسه پیگیری: <span dir='ltr'>{failure.traceId}</span>
            </small>
          )}
          {failure.existingBusiness && (
            <button
              type='button'
              className='btn btn-sm btn-light-primary mt-3'
              disabled={busy !== null}
              onClick={() => openBusiness(failure.existingBusiness!.companyId, false)}
            >
              ورود به «{failure.existingBusiness.name}»
            </button>
          )}
          {extra}
        </div>
      </div>
    ) : null

  // Before the panel is made, the taxpayer may give up and go back to the partner.
  const partnerCancel =
    partner && !business ? (
      confirmCancel ? (
        <div className='olr-partner-cancel' role='group' aria-label='لغو ثبت‌نام'>
          <span>ثبت‌نام لغو شود{partner.hasReturnUrl ? ` و به ${partner.partnerName} برگردید` : ''}؟</span>
          <button type='button' className='btn btn-sm btn-danger' disabled={busy !== null} onClick={cancelPartner}>
            {busy === 'open' ? <Spinner label='در حال لغو…' /> : 'بله، لغو شود'}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light'
            disabled={busy !== null}
            onClick={() => setConfirmCancel(false)}
          >
            ادامه ثبت‌نام
          </button>
        </div>
      ) : (
        <button
          type='button'
          className='btn btn-light'
          disabled={busy !== null}
          onClick={() => setConfirmCancel(true)}
        >
          <i className='las la-times' aria-hidden='true' /> انصراف
        </button>
      )
    ) : null

  const header = (
    <header className='business-registration__header'>
      <div>
        <span className='business-registration__eyebrow'>
          {partner ? `ثبت‌نام به درخواست ${partner.partnerName}` : 'ثبت‌نام آنلاین'}
        </span>
        <h1>{noBusiness || partner ? 'ساخت پنل کسب‌وکار' : 'افزودن کسب‌وکار'}</h1>
        <p>
          مالکیت شما با رمزی که سامانه مؤدیان می‌فرستد تأیید می‌شود؛ پنل در چند دقیقه ساخته و شناسه
          یکتای حافظه مالیاتی هم گرفته می‌شود.
          {partner && ` پس از پایان، به ${partner.partnerName} برمی‌گردید.`}
        </p>
      </div>
      {partner ? (
        partnerCancel
      ) : noBusiness ? (
        <RegistrationSignOut />
      ) : (
        <Link to='/dashboard' className='btn btn-light'>
          <i className='las la-arrow-right' aria-hidden='true' /> بازگشت به داشبورد
        </Link>
      )}
    </header>
  )

  if (availabilityState === 'loading') {
    return (
      <main className='business-registration' dir='rtl'>
        {header}
        <div className='olr-loading' role='status'>
          <Spinner label='در حال آماده‌سازی…' />
        </div>
      </main>
    )
  }

  if (availabilityState === 'unavailable') {
    return (
      <main className='business-registration' dir='rtl'>
        {header}
        <div className='business-alert business-alert--warning' role='status'>
          <i className='las la-info-circle' aria-hidden='true' />
          <div>
            <strong>{unavailableMessage || 'ثبت‌نام آنلاین در حال حاضر در دسترس نیست.'}</strong>
            <div className='olr-actions-inline'>
              {!partner && (
                <a href={CONSULTATION_PATH} className='btn btn-sm btn-primary'>
                  <i className='las la-headset' aria-hidden='true' /> درخواست مشاوره رایگان
                </a>
              )}
              {ssoUser && !partner && (
                <Link to={SSO_BUSINESS_REGISTRATION_PATH} className='btn btn-sm btn-light-primary'>
                  ثبت با شناسه یکتای موجود
                </Link>
              )}
            </div>
            <small className='sso-registration__note'>{supportContact}</small>
          </div>
        </div>
      </main>
    )
  }

  const currentIndex = stepIndex(step)
  const otpLength = delegation?.otpLength || 6
  const otpRemaining = delegation?.otpExpiresAt ? delegation.otpExpiresAt - now : 0
  const codeLive = Boolean(delegation?.otpPending && otpRemaining > 0)
  const identifierHint = describeIdentifier(identifier)
  const delegationUntil = formatJalaliDate(business?.delegation?.expiresAt ?? delegation?.expiresAt)

  return (
    <main className='business-registration olr' dir='rtl'>
      {header}

      <ol className='olr-steps' aria-label='مراحل ثبت‌نام'>
        {ONLINE_STEPS.map((item, index) => (
          <li
            key={item.key}
            className={
              index === currentIndex ? 'is-current' : index < currentIndex ? 'is-done' : undefined
            }
            aria-current={index === currentIndex ? 'step' : undefined}
          >
            <span className='olr-steps__number'>
              {index < currentIndex ? (
                <i className='las la-check' aria-hidden='true' />
              ) : (
                toPersianNumber(index + 1)
              )}
            </span>
            <span className='olr-steps__title'>{item.title}</span>
          </li>
        ))}
      </ol>

      {step === 'identify' && (
        <section className='business-section' aria-labelledby='olr-identify-title'>
          <div className='business-section__heading'>
            <span>{toPersianNumber(1)}</span>
            <div>
              <h2 id='olr-identify-title' ref={headingRef} tabIndex={-1}>
                پرونده مالیاتی کسب‌وکار
              </h2>
              <p>
                {fixedEconomicCode
                  ? `${partner?.partnerName} این لینک را برای همین کسب‌وکار ساخته است؛ پرونده آن از سامانه مؤدیان خوانده می‌شود.`
                  : ssoUser && !partner
                  ? 'پرونده‌های هویتی که با آن از کارپوشه وارد شده‌اید در زیر آمده است؛ پرونده دیگری را هم می‌توانید جست‌وجو کنید.'
                  : 'شماره اقتصادی یا کد ملی/شناسه ملی صاحب کسب‌وکار را وارد کنید.'}
              </p>
            </div>
          </div>

          {fixedEconomicCode && (
            <div className='olr-fixed'>
              <div className='business-alert business-alert--info' role='status'>
                <i className='las la-store' aria-hidden='true' />
                <div>
                  <strong>
                    شماره اقتصادی{' '}
                    <bdi dir='ltr' className='englishText'>
                      {fixedEconomicCode}
                    </bdi>
                  </strong>
                  {busy === 'start' && (
                    <small className='sso-registration__note'>
                      <Spinner label='در حال خواندن پرونده از سامانه مؤدیان…' />
                    </small>
                  )}
                </div>
              </div>
              {failureBox(
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary mt-3'
                  disabled={busy !== null}
                  onClick={() => choose({economicCode: fixedEconomicCode})}
                >
                  تلاش دوباره
                </button>
              )}
            </div>
          )}

          {!fixedEconomicCode && ssoUser && !partner && availability?.ssoIdentifier && (
            <div className='business-alert business-alert--info' role='note'>
              <i className='las la-landmark' aria-hidden='true' />
              <div>
                <strong>
                  ورود با کارپوشه مالیاتی:{' '}
                  <span dir='ltr' className='sso-registration__nid'>
                    {availability.ssoIdentifier}
                  </span>
                </strong>
              </div>
            </div>
          )}

          {!fixedEconomicCode && (
            <form
              className='fiscal-inquiry-row'
              onSubmit={(event) => {
                event.preventDefault()
                if (isSearchableIdentifier(identifier)) search(identifier)
              }}
              noValidate
            >
              <div className='business-field'>
                <label htmlFor='olrIdentifier'>شماره اقتصادی، کد ملی یا شناسه ملی</label>
                <input
                  id='olrIdentifier'
                  dir='ltr'
                  inputMode='numeric'
                  autoComplete='off'
                  autoFocus={!ssoUser || Boolean(partner)}
                  placeholder='مثلاً 14000000000'
                  value={identifier}
                  disabled={busy !== null}
                  aria-describedby='olrIdentifier-help'
                  onChange={(event) => setIdentifier(normalizeIdentifierInput(event.target.value))}
                />
                <small id='olrIdentifier-help'>
                  {identifierHint || 'فقط رقم، بدون خط تیره؛ رقم فارسی هم پذیرفته می‌شود.'}
                </small>
              </div>
              <button
                type='submit'
                className='btn btn-primary business-inquiry-button'
                disabled={busy !== null || !isSearchableIdentifier(identifier)}
              >
                {busy === 'identify' ? (
                  <Spinner label='در حال جست‌وجو…' />
                ) : (
                  <>
                    <i className='las la-search' aria-hidden='true' /> جست‌وجوی پرونده
                  </>
                )}
              </button>
            </form>
          )}

          {busy === 'identify' && !files && (
            <div className='olr-loading' role='status'>
              <Spinner label='در حال خواندن پرونده‌ها از سامانه مؤدیان…' />
            </div>
          )}

          {!fixedEconomicCode && failureBox()}

          {files && !fixedEconomicCode && (
            <ul className='olr-files' aria-label='پرونده‌های پیدا شده'>
              {files.items.map((file) => (
                <li key={file.economicCode} className='olr-file'>
                  <div className='olr-file__main'>
                    <strong>{file.brand || file.companyName || 'پرونده مالیاتی'}</strong>
                    <span className='olr-file__code'>
                      شماره اقتصادی <bdi dir='ltr'>{file.economicCode}</bdi>
                    </span>
                    <div className='olr-file__chips'>
                      <span
                        className={
                          file.taxFileStatus === 'ACTIVE' ? 'olr-chip olr-chip--ok' : 'olr-chip olr-chip--warn'
                        }
                      >
                        کارپوشه: {file.taxFileStatusTitle || 'نامشخص'}
                      </span>
                      {file.callStatusTitle && <span className='olr-chip'>{file.callStatusTitle}</span>}
                      {file.panelState === 'yours' && (
                        <span className='olr-chip olr-chip--info'>در پنل شما</span>
                      )}
                      {file.panelState === 'other' && (
                        <span className='olr-chip olr-chip--warn'>پیش‌تر در آسا ثبت شده</span>
                      )}
                    </div>
                    {!file.selectable && file.reason && <p className='olr-file__reason'>{file.reason}</p>}
                  </div>
                  {file.selectable && (
                    <button
                      type='button'
                      className={file.panelState === 'yours' ? 'btn btn-light-primary' : 'btn btn-primary'}
                      disabled={busy !== null}
                      onClick={() => choose(file)}
                    >
                      {busy === 'start' && choosing === file.economicCode ? (
                        <Spinner label='در حال بررسی…' />
                      ) : file.panelState === 'yours' ? (
                        'تکمیل راه‌اندازی'
                      ) : (
                        <>
                          انتخاب و ادامه <i className='las la-arrow-left' aria-hidden='true' />
                        </>
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {ssoUser && !partner && (
            <p className='olr-muted'>
              شناسه یکتای حافظه مالیاتی دارید و به موبایل ثبت‌شده در کارپوشه دسترسی ندارید؟{' '}
              <Link to={SSO_BUSINESS_REGISTRATION_PATH}>ثبت با شناسه یکتای موجود</Link>
            </p>
          )}
          <p className='olr-muted'>
            کسب‌وکاری که پیش‌تر در آسا ثبت شده دوباره ساخته نمی‌شود. اگر پنل آن را کس دیگری (مثلاً
            حسابدارتان) ساخته است، مدیر همان پنل می‌تواند شما را به کاربران آن اضافه کند.
          </p>
        </section>
      )}

      {step === 'owner' && session && (
        <section className='business-section' aria-labelledby='olr-owner-title'>
          <div className='business-section__heading'>
            <span>{toPersianNumber(2)}</span>
            <div>
              <h2 id='olr-owner-title' ref={headingRef} tabIndex={-1}>
                تأیید مالکیت با رمز سامانه مؤدیان
              </h2>
              <p>
                سامانه مؤدیان رمز یک‌بارمصرف را به موبایلی می‌فرستد که در کارپوشه مالیاتی این مؤدی ثبت
                شده است. با وارد کردن آن، مالکیت شما تأیید و دسترسی آسا (شرکت معتمد) به کارپوشه برای
                یک سال فعال می‌شود.
              </p>
            </div>
          </div>

          <div className='verified-business' role='status'>
            <div className='verified-business__title'>
              <i className='las la-check-circle' aria-hidden='true' />
              <div>
                <strong>{session.companyName || session.taxpayerName}</strong>
                <small>
                  {session.mode === 'complete'
                    ? 'این کسب‌وکار در پنل شماست؛ راه‌اندازی آن کامل می‌شود.'
                    : 'اطلاعات از استعلام رسمی خوانده شد.'}
                </small>
              </div>
            </div>
            <dl>
              <div>
                <dt>نام مؤدی</dt>
                <dd>{session.taxpayerName}</dd>
              </div>
              <div>
                <dt>نوع مؤدی</dt>
                <dd>{session.taxpayerTypeTitle}</dd>
              </div>
              <div>
                <dt>شماره اقتصادی</dt>
                <dd dir='ltr'>{session.economicCode}</dd>
              </div>
              <div>
                <dt>شناسه ملی / کد ملی</dt>
                <dd dir='ltr'>{session.nationalIdentifier}</dd>
              </div>
              <div>
                <dt>وضعیت کارپوشه</dt>
                <dd className='text-success'>{session.taxFileStatusTitle || 'فعال'}</dd>
              </div>
            </dl>
          </div>

          <details className='olr-permissions'>
            <summary>
              دسترسی‌هایی که با این رمز فعال می‌شود ({toPersianNumber(session.permissions.length)})
            </summary>
            <ul>
              {session.permissions.map((permission) => (
                <li key={permission}>{PERMISSION_LABELS[permission] || permission}</li>
              ))}
            </ul>
          </details>

          {session.mode === 'create' && (
            <div className='sso-registration__terms'>
              <div className='form-check form-check-custom form-check-solid'>
                <input
                  id='olrTermsAccepted'
                  type='checkbox'
                  className='form-check-input'
                  checked={termsAccepted}
                  disabled={busy !== null}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                />
                <label className='form-check-label' htmlFor='olrTermsAccepted'>
                  قوانین و مقررات استفاده از خدمات
                  {terms ? ` (${formatTermsVersion(terms.version)})` : ''} را خوانده‌ام و می‌پذیرم.
                </label>
              </div>
              <TermsViewerButton className='fw-semibold'>
                <i className='las la-file-contract fs-4 me-1' aria-hidden='true' />
                خواندن متن قوانین و مقررات
              </TermsViewerButton>
            </div>
          )}

          {!codeLive && (
            <div className='olr-send'>
              <button
                type='button'
                className='btn btn-primary btn-lg'
                disabled={busy !== null || (session.mode === 'create' && !termsAccepted)}
                title={
                  session.mode === 'create' && !termsAccepted
                    ? 'ابتدا قوانین و مقررات را بپذیرید.'
                    : undefined
                }
                onClick={sendCode}
              >
                {busy === 'otp' ? (
                  <Spinner label='در حال ارسال…' />
                ) : (
                  <>
                    <i className='las la-sms' aria-hidden='true' />{' '}
                    {delegation?.otpExpiresAt ? 'ارسال دوباره رمز' : 'ارسال رمز به موبایل ثبت‌شده در کارپوشه'}
                  </>
                )}
              </button>
              {delegation?.otpExpiresAt && otpRemaining <= 0 && (
                <small className='olr-muted'>اعتبار رمز قبلی تمام شد؛ رمز تازه بگیرید.</small>
              )}
            </div>
          )}

          {codeLive && (
            <form className='olr-code' onSubmit={verify} noValidate>
              <p role='status' aria-live='polite'>
                رمز {toPersianNumber(otpLength)} رقمی به شماره{' '}
                <bdi dir='ltr' className='olr-mobile'>
                  {delegation?.maskedMobile || 'ثبت‌شده در کارپوشه'}
                </bdi>{' '}
                فرستاده شد. اعتبار رمز: <span className='olr-countdown'>{formatRemaining(otpRemaining)}</span>
              </p>
              <div className='olr-code__row'>
                <label htmlFor='olrCode' className='visually-hidden'>
                  رمز یک‌بارمصرف
                </label>
                <input
                  id='olrCode'
                  className='olr-otp'
                  dir='ltr'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  autoFocus
                  maxLength={otpLength}
                  placeholder={'•'.repeat(otpLength)}
                  value={otp}
                  disabled={busy !== null}
                  onChange={(event) => setOtp(normalizeOtpInput(event.target.value, otpLength))}
                />
                <button
                  type='submit'
                  className='btn btn-success btn-lg'
                  disabled={busy !== null || !isCompleteOtp(otp, otpLength)}
                >
                  {busy === 'verify' ? (
                    <Spinner label={session.mode === 'create' ? 'در حال ساخت پنل…' : 'در حال تأیید…'} />
                  ) : session.mode === 'create' ? (
                    <>
                      <i className='las la-check-double' aria-hidden='true' /> تأیید و ساخت پنل
                    </>
                  ) : (
                    <>
                      <i className='las la-check-double' aria-hidden='true' /> تأیید و فعال‌سازی دسترسی
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {failureBox()}

          <details className='olr-help'>
            <summary>به موبایل ثبت‌شده در کارپوشه دسترسی ندارید؟</summary>
            <p>
              رمز فقط به موبایلی می‌رود که در پرونده مالیاتی ثبت شده است. شماره را می‌توانید در
              کارپوشه مالیاتی (my.tax.gov.ir) یا سامانه ثبت‌نام مالیاتی به‌روز کنید و دوباره رمز
              بگیرید.
              {ssoUser && !partner && (
                <>
                  {' '}
                  اگر شناسه یکتای حافظه مالیاتی دارید،{' '}
                  <Link to={SSO_BUSINESS_REGISTRATION_PATH}>با همان شناسه ثبت کنید</Link>.
                </>
              )}{' '}
              {supportContact}
            </p>
          </details>

          <div className='olr-back'>
            <button type='button' className='btn btn-link' disabled={busy !== null} onClick={() => restart()}>
              <i className='las la-arrow-right' aria-hidden='true' /> انتخاب پرونده دیگر
            </button>
          </div>
        </section>
      )}

      {step === 'memory' && business && (
        <section className='business-section' aria-labelledby='olr-memory-title'>
          <div className='business-section__heading'>
            <span>{toPersianNumber(3)}</span>
            <div>
              <h2 id='olr-memory-title' ref={headingRef} tabIndex={-1}>
                شناسه یکتای حافظه مالیاتی
              </h2>
              <p>برای صدور صورتحساب، کسب‌وکار به شناسه یکتای حافظه مالیاتی با شرکت معتمد آسا نیاز دارد.</p>
            </div>
          </div>

          <div className='business-alert business-alert--info' role='status'>
            <i className='las la-check-circle' aria-hidden='true' />
            <div>
              <strong>
                {business.created
                  ? `پنل «${business.companyName || 'کسب‌وکار'}» ساخته شد و شما مدیر کامل آن هستید.`
                  : `دسترسی کارپوشه «${business.companyName || 'کسب‌وکار'}» فعال است.`}
              </strong>
              {delegationUntil && (
                <small className='sso-registration__note'>دسترسی آسا به کارپوشه تا {delegationUntil} معتبر است.</small>
              )}
            </div>
          </div>

          <div className='olr-options' role='radiogroup' aria-label='روش ثبت شناسه یکتا'>
            {business.memoryUniqueIdAvailable && (
              <label className={memoryMode === 'issue' ? 'olr-option is-selected' : 'olr-option'}>
                <input
                  type='radio'
                  name='olrMemoryMode'
                  checked={memoryMode === 'issue'}
                  onChange={() => setMemoryMode('issue')}
                />
                <span>
                  <strong>دریافت شناسه یکتای جدید از سامانه مؤدیان (پیشنهادی)</strong>
                  <small>آسا به‌عنوان شرکت معتمد، شناسه تازه را مستقیم از سامانه مؤدیان می‌گیرد و ثبت می‌کند.</small>
                </span>
              </label>
            )}
            <label
              className={
                memoryMode === 'existing' || !business.memoryUniqueIdAvailable
                  ? 'olr-option is-selected'
                  : 'olr-option'
              }
            >
              <input
                type='radio'
                name='olrMemoryMode'
                checked={memoryMode === 'existing' || !business.memoryUniqueIdAvailable}
                onChange={() => setMemoryMode('existing')}
              />
              <span>
                <strong>شناسه یکتای فعلی را دارم</strong>
                <small>شناسه‌ای که در کارپوشه با شرکت معتمد آسا ساخته‌اید؛ با استعلام رسمی بررسی می‌شود.</small>
              </span>
            </label>
          </div>

          {business.memoryUniqueIdAvailable && memoryMode === 'issue' ? (
            <div className='olr-send'>
              <button type='button' className='btn btn-success btn-lg' disabled={busy !== null} onClick={issueMemory}>
                {busy === 'memory' ? (
                  <Spinner label='در حال دریافت از سامانه مؤدیان…' />
                ) : (
                  <>
                    <i className='las la-microchip' aria-hidden='true' /> دریافت شناسه یکتا
                  </>
                )}
              </button>
            </div>
          ) : (
            <form className='fiscal-inquiry-row' onSubmit={addMemory} noValidate>
              <div className='business-field'>
                <label htmlFor='olrFiscalId'>شناسه یکتای حافظه مالیاتی</label>
                <input
                  id='olrFiscalId'
                  className='englishText'
                  dir='ltr'
                  maxLength={6}
                  autoCapitalize='characters'
                  autoComplete='off'
                  placeholder='A1B2C3'
                  value={fiscalId}
                  disabled={busy !== null}
                  onChange={(event) => setFiscalId(normalizeFiscalId(event.target.value))}
                />
                <small>۶ نویسه، حروف بزرگ انگلیسی و رقم.</small>
              </div>
              <button
                type='submit'
                className='btn btn-success business-inquiry-button'
                disabled={busy !== null || !isCompleteFiscalIdValue(fiscalId)}
              >
                {busy === 'memory' ? <Spinner label='در حال بررسی…' /> : 'بررسی و ثبت'}
              </button>
            </form>
          )}

          {failureBox(
            failure?.newCode && session?.economicCode ? (
              <button
                type='button'
                className='btn btn-sm btn-light-primary mt-3'
                disabled={busy !== null}
                onClick={() => choose({economicCode: session.economicCode})}
              >
                دریافت دسترسی تازه با رمز سامانه مؤدیان
              </button>
            ) : undefined
          )}

          <div className='olr-back'>
            <button type='button' className='btn btn-link' disabled={busy !== null} onClick={() => setStep('done')}>
              بعداً ثبت می‌کنم
            </button>
          </div>
        </section>
      )}

      {step === 'done' && business && (
        <section className='business-section olr-done' aria-labelledby='olr-done-title'>
          <i className='las la-check-circle olr-done__icon' aria-hidden='true' />
          <h2 id='olr-done-title' ref={headingRef} tabIndex={-1}>
            {memory?.memoryUniqueId || !business.created ? 'پنل شما آماده است' : 'پنل شما ساخته شد'}
          </h2>
          <dl className='olr-done__summary'>
            <div>
              <dt>کسب‌وکار</dt>
              <dd>{business.companyName}</dd>
            </div>
            {session?.economicCode && (
              <div>
                <dt>شماره اقتصادی</dt>
                <dd dir='ltr'>{session.economicCode}</dd>
              </div>
            )}
            <div>
              <dt>شناسه یکتا</dt>
              <dd dir='ltr' className='englishText'>{memory?.memoryUniqueId || '—'}</dd>
            </div>
            {delegationUntil && (
              <div>
                <dt>دسترسی کارپوشه</dt>
                <dd>تا {delegationUntil}</dd>
              </div>
            )}
          </dl>
          {memory && !memory.companyTokenId && memory.issued && (
            <div className='business-alert business-alert--warning' role='status'>
              <i className='las la-exclamation-triangle' aria-hidden='true' />
              <div>
                <strong>
                  شناسه یکتا گرفته شد ولی در پنل ذخیره نشد؛ آن را در «تنظیمات کسب‌وکار › شناسه یکتا» با
                  همین مقدار اضافه کنید.
                </strong>
              </div>
            </div>
          )}
          {!memory?.memoryUniqueId && (
            <p className='olr-muted'>
              برای صدور صورتحساب، شناسه یکتا را بعداً از «تنظیمات کسب‌وکار › شناسه یکتا» اضافه کنید.
            </p>
          )}
          {failureBox(
            partner && partnerReturn.state === 'failed' ? (
              <button
                type='button'
                className='btn btn-sm btn-light-primary mt-3'
                onClick={() => {
                  setFailure(undefined)
                  setPartnerReturn({state: 'idle'})
                }}
              >
                تلاش دوباره
              </button>
            ) : undefined
          )}
          {partner ? (
            <div className='olr-partner-return'>
              {partnerReturn.state === 'finishing' && (
                <div className='olr-loading' role='status'>
                  <Spinner label={`در حال آماده‌سازی بازگشت به ${partner.partnerName}…`} />
                </div>
              )}
              {partnerReturn.state === 'ready' && partnerReturn.url && (
                <button type='button' className='btn btn-success btn-lg' disabled={busy !== null} onClick={returnToPartner}>
                  بازگشت به {partner.partnerName} <i className='las la-arrow-left' aria-hidden='true' />
                </button>
              )}
              {partnerReturn.state === 'ready' && !partnerReturn.url && (
                <p className='olr-muted' role='status'>
                  ثبت‌نام کامل شد و {partner.partnerName} از آن باخبر شد؛ می‌توانید این صفحه را ببندید.
                </p>
              )}
              <button
                type='button'
                className={partnerReturn.url ? 'btn btn-light-primary' : 'btn btn-success btn-lg'}
                disabled={busy !== null || partnerReturn.state === 'finishing'}
                onClick={() => openBusiness(business.companyId, business.created)}
              >
                {busy === 'open' ? <Spinner label='در حال ورود…' /> : 'ورود به پنل معتمد آسا'}
              </button>
            </div>
          ) : (
            <button
              type='button'
              className='btn btn-success btn-lg'
              disabled={busy !== null}
              onClick={() => openBusiness(business.companyId, business.created)}
            >
              {busy === 'open' ? (
                <Spinner label='در حال ورود…' />
              ) : (
                <>
                  ورود به پنل <i className='las la-arrow-left' aria-hidden='true' />
                </>
              )}
            </button>
          )}
        </section>
      )}
    </main>
  )
}

export {OnlineBusinessRegistration}
